import { useReducer, createContext, useContext } from 'react';

import { mergeResults, getTotalMatches, buildRows, getResults, isExactMatch } from './GlobalSearchUtils';

const dispatchContext = createContext();
const storeContext = createContext();

const ACTIONS = {
    RESET: 'reset',
    BEGIN_LOAD: 'beginLoad',
    SET_SEARCH: 'setSearch',
    SET_ERROR: 'setError',
    SET_RESULTS: 'setResults',
    APPEND_RESULTS: 'appendResults',
};

const ACTION_HANDLERS = {
    [ACTIONS.RESET]: dispatchReset,
    [ACTIONS.BEGIN_LOAD]: dispatchBeginLoad,
    [ACTIONS.SET_ERROR]: dispatchSetError,
    [ACTIONS.SET_RESULTS]: dispatchSetResults,
    [ACTIONS.APPEND_RESULTS]: dispatchAppendResults,
};

export function GlobalSearchStoreProvider(props) {
    const { children } = props;

    const [store, dispatch] = useReducer(storeDispatcher, null, buildInitialStoreState);

    return (
        <dispatchContext.Provider value={dispatch}>
            <storeContext.Provider value={store}>{children}</storeContext.Provider>
        </dispatchContext.Provider>
    );
}

export function useDispatcher() {
    return useContext(dispatchContext);
}

export function useStore() {
    return useContext(storeContext);
}

export const GlobalSearchDispatchConsumer = dispatchContext.Consumer;
export const GlobalSearchStoreConsumer = storeContext.Consumer;

// shortcut/async functions
export const Actions = {
    reset: (dispatcher) => dispatcher({ type: ACTIONS.RESET }),

    search: (dispatcher, search, isPgm, userGroups) => {
        dispatcher({ type: ACTIONS.BEGIN_LOAD, params: { search, userGroups } });

        getResults(search, { isPgm, userGroups })
            .then(({ results }) => {
                return dispatcher({ type: ACTIONS.SET_RESULTS, params: { results } });
            })
            .catch((error) => dispatcher({ type: ACTIONS.SET_ERROR, params: { error } }));
    },

    loadMore: (dispatcher, search, result, isPgm, userGroups) => {
        dispatcher({ type: ACTIONS.BEGIN_LOAD, params: { isNestedLoading: true } });

        const { type, start, limit, cursor } = result;

        getResults(search, { type, isPgm, userGroups, rowStart: start + limit, rowLimit: 50, cursor })
            .then(({ results }) => {
                return dispatcher({ type: ACTIONS.APPEND_RESULTS, params: { results } });
            })
            .catch((error) => dispatcher({ type: ACTIONS.SET_ERROR, params: { error } }));
    },
};

function buildInitialStoreState() {
    return {
        search: null,
        isLoading: false,
        isNestedLoading: false, // used for "show more"
        error: null,
        totalMatches: 0,
        exactMatches: null,
        results: [],
        rows: [],
    };
}

function storeDispatcher(store, { type, params }) {
    return ACTION_HANDLERS[type](store, params);
}

function dispatchReset() {
    return buildInitialStoreState();
}

function dispatchBeginLoad(store, params) {
    return { ...store, isLoading: true, ...(params || {}) };
}

function dispatchSetError(store, { error }) {
    return {
        ...store,

        isLoading: false,
        error,
    };
}

function dispatchSetResults(store, { results }) {
    return {
        ...store,

        isLoading: false,
        isNestedLoading: false,
        error: null,
        results,
        totalMatches: getTotalMatches(results),
        exactMatches: isExactMatch(results),
        rows: buildRows(results),
    };
}

function dispatchAppendResults(store, { results: newResults }) {
    const results = mergeResults(store.results, newResults);

    return {
        ...store,

        isLoading: false,
        isNestedLoading: false,
        results,
        totalMatches: getTotalMatches(results),
        rows: buildRows(results),
    };
}
